import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { escapeRegex } from '@adornis/filter/aspect-schemas/escapeRegex.js';
import { gateNexusSubscription } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { from, switchMap } from 'rxjs';
import { RouteOptimizationResult, type simulationType } from '../entities/RouteOptimizationResult.js';

/**
 * @entity TicketStatistic
 */
@Entity()
export class TicketStatistic extends AdornisEntity {
  static override _class = 'TicketStatistic';

  static override resolveID(entity: TicketStatistic): string {
    return entity.ticketID;
  }

  @Field(_type => String) ticketID!: string;
  @Field(_type => Int) numAutomaticDryRuns: Maybe<number>;
  @Field(_type => Int) numAutomaticRuns: Maybe<number>;
  @Field(_type => Int) numAutomaticPlanning: Maybe<number>;
  @Field(_type => Int) numAutomaticScheduling: Maybe<number>;
  @Field(_type => Int) numRerunRuns: Maybe<number>;
  @Field(_type => Int) numRerunPlanning: Maybe<number>;
  @Field(_type => Int) numManualDryRuns: Maybe<number>;
  @Field(_type => Int) numManualRuns: Maybe<number>;
  @Field(_type => Int) numManualPlanning: Maybe<number>;
  @Field(_type => Int) numManualScheduling: Maybe<number>;
  @Field(_type => Int) numTotalPlanning: Maybe<number>;
  @Field(_type => Int) numTotalScheduling: Maybe<number>;
  @Field(_type => Int) numTotalRuns: Maybe<number>;
}

@Entity()
export class TicketPlanningGlobalStatistic extends AdornisEntity {
  static override _class = 'TicketPlanningGlobalStatistic';

  static override resolveID(entity: TicketPlanningGlobalStatistic): string {
    return entity.simulationType;
  }

  @Field(_type => String) simulationType!: simulationType | 'rerun';
  @Field(_type => Int) plannedTicketsCount: Maybe<number>;
}

@Entity()
export class TicketStatsFilter extends AdornisFilter {
  static override _class = 'TicketStatsFilter';

  @Field(_type => Int) month: Maybe<number>;
  @Field(_type => Int) year: Maybe<number>;

  constructor(doc: PartialEntityData<TicketStatsFilter>) {
    super(doc as PartialEntityData<AdornisFilter>);
  }
}

const getCreatedAtMatch = (month: number | undefined, year: number | undefined) => {
  if (!year || year <= 0) return undefined;

  if (month && month > 0) {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 1);
    return { createdAt: { $gte: start, $lt: end } };
  }

  const start = new Date(year, 0, 1);
  const end = new Date(year + 1, 0, 1);
  return { createdAt: { $gte: start, $lt: end } };
};

const unassignedTicketID = 'unassigned';

export const getCurrentTicketStatistics = registerSubscription({
  operationName: 'getCurrentTicketStatistics',
  type: () => [TicketStatistic],
  resolve:
    (skip: number, limit: number, filter: TicketStatsFilter) => (_gqlFields: BaseQLSelectionSet<TicketStatistic>) => {
      const month = filter.month ?? undefined;
      const year = filter.year ?? undefined;
      const createdAtMatch = getCreatedAtMatch(month, year);

      return gateNexusSubscription(NexusPermission.ClientStats, () =>
        from(getRawCollection<EntityData<RouteOptimizationResult>>(RouteOptimizationResult._collectionName)).pipe(
          switchMap(async col => {
            const results = await col
              .aggregate<{
                _id: string;
                numRerunRuns: number;
                numRerunPlanning: number;
                numAutomaticScheduling: number;
                numAutomaticPlanning: number;
                numManualScheduling: number;
                numManualPlanning: number;
                numTotalScheduling: number;
                numTotalPlanning: number;
              }>([
                { $match: { simulationType: { $ne: null } } },
                ...(createdAtMatch ? [{ $match: createdAtMatch }] : []),
                {
                  $facet: {
                    assigned: [
                      { $unwind: '$routes' },
                      { $unwind: '$routes.stops' },
                      {
                        $match: {
                          'routes.stops.ticketId': { $ne: null },
                        },
                      },
                      {
                        $group: {
                          _id: '$routes.stops.ticketId',
                          numRerunRuns: {
                            $sum: {
                              $cond: [{ $regexMatch: { input: '$simulationType', regex: /^rerun-/ } }, 1, 0],
                            },
                          },
                          numAutomaticDryRuns: {
                            $sum: { $cond: [{ $eq: ['$simulationType', 'automatic-dry-run'] }, 1, 0] },
                          },
                          numAutomaticRuns: {
                            $sum: { $cond: [{ $eq: ['$simulationType', 'automatic-run'] }, 1, 0] },
                          },
                          numManualDryRuns: {
                            $sum: { $cond: [{ $eq: ['$simulationType', 'manual-dry-run'] }, 1, 0] },
                          },
                          numManualRuns: {
                            $sum: { $cond: [{ $eq: ['$simulationType', 'manual-run'] }, 1, 0] },
                          },
                          numTotalRuns: {
                            $sum: 1,
                          },
                        },
                      },
                    ],
                    unassigned: [
                      { $unwind: '$unscheduledTickets' },
                      { $match: { 'unscheduledTickets.ticketId': { $ne: null } } },
                      {
                        $group: {
                          _id: '$simulationType',
                          ticketIds: { $addToSet: '$unscheduledTickets.ticketId' },
                        },
                      },
                      {
                        $project: {
                          simulationType: '$_id',
                          count: {
                            $size: {
                              $filter: {
                                input: '$ticketIds',
                                as: 'id',
                                cond: { $and: [{ $ne: ['$$id', null] }, { $ne: ['$$id', ''] }] },
                              },
                            },
                          },
                        },
                      },
                      {
                        $group: {
                          _id: { $literal: unassignedTicketID },
                          numRerunRuns: {
                            $sum: {
                              $cond: [{ $regexMatch: { input: '$simulationType', regex: /^rerun-/ } }, '$count', 0],
                            },
                          },
                          numAutomaticDryRuns: {
                            $sum: {
                              $cond: [{ $eq: ['$simulationType', 'automatic-dry-run'] }, '$count', 0],
                            },
                          },
                          numAutomaticRuns: {
                            $sum: {
                              $cond: [{ $eq: ['$simulationType', 'automatic-run'] }, '$count', 0],
                            },
                          },
                          numManualDryRuns: {
                            $sum: {
                              $cond: [{ $eq: ['$simulationType', 'manual-dry-run'] }, '$count', 0],
                            },
                          },
                          numManualRuns: {
                            $sum: {
                              $cond: [{ $eq: ['$simulationType', 'manual-run'] }, '$count', 0],
                            },
                          },
                          numTotalRuns: { $sum: '$count' },
                        },
                      },
                    ],
                  },
                },
                {
                  $project: {
                    combined: {
                      $concatArrays: [
                        '$assigned',
                        [
                          {
                            $ifNull: [
                              { $arrayElemAt: ['$unassigned', 0] },
                              {
                                _id: unassignedTicketID,
                                numRerunRuns: 0,
                                numAutomaticDryRuns: 0,
                                numAutomaticRuns: 0,
                                numManualDryRuns: 0,
                                numManualRuns: 0,
                                numTotalRuns: 0,
                              },
                            ],
                          },
                        ],
                      ],
                    },
                  },
                },
                { $unwind: '$combined' },
                { $replaceRoot: { newRoot: '$combined' } },
                { $match: { _id: { $ne: 'unassigned' } } },
                {
                  $project: {
                    ticketID: '$_id',
                    numRerunRuns: '$numRerunRuns',
                    numRerunPlanning: '$numRerunRuns',
                    numAutomaticScheduling: '$numAutomaticRuns',
                    numManualScheduling: '$numManualRuns',
                    numAutomaticPlanning: { $add: ['$numAutomaticDryRuns', '$numAutomaticRuns'] },
                    numManualPlanning: { $add: ['$numManualDryRuns', '$numManualRuns'] },
                    numTotalScheduling: { $add: ['$numAutomaticRuns', '$numManualRuns'] },
                    numTotalPlanning: {
                      $add: ['$numAutomaticDryRuns', '$numAutomaticRuns', '$numManualDryRuns', '$numManualRuns'],
                    },
                  },
                },
                { $sort: filter.getMongoOptions().sort },
                ...(filter.search ? [{ $match: { _id: { $regex: escapeRegex(filter.search), $options: 'i' } } }] : []),
                { $skip: skip ?? 0 },
                { $limit: limit ?? 100 },
              ])
              .toArray();

            const stats = results.map(
              r =>
                new TicketStatistic({
                  ticketID: r._id,
                  numRerunRuns: r.numRerunRuns,
                  numRerunPlanning: r.numRerunPlanning,
                  numAutomaticScheduling: r.numAutomaticScheduling,
                  numAutomaticPlanning: r.numAutomaticPlanning,
                  numManualScheduling: r.numManualScheduling,
                  numManualPlanning: r.numManualPlanning,
                  numTotalScheduling: r.numTotalScheduling,
                  numTotalPlanning: r.numTotalPlanning,
                }),
            );
            return stats;
          }),
        ),
      );
    },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => TicketStatsFilter },
  ],
});

export const getTicketPlanningGlobalStatistics = registerSubscription({
  operationName: 'getTicketPlanningGlobalStatistics',
  type: () => [TicketPlanningGlobalStatistic],
  resolve: (month: number, year: number) => (_gqlFields: BaseQLSelectionSet<TicketPlanningGlobalStatistic>) => {
    const createdAtMatch = getCreatedAtMatch(month, year);

    return gateNexusSubscription(NexusPermission.ClientStats, () =>
      from(getRawCollection<EntityData<RouteOptimizationResult>>(RouteOptimizationResult._collectionName)).pipe(
        switchMap(async col => {
          const results = await col
            .aggregate<{
              _id: string;
              plannedTicketsCount: number;
            }>([
              { $match: { simulationType: { $ne: null } } },
              ...(createdAtMatch ? [{ $match: createdAtMatch }] : []),
              { $unwind: '$routes' },
              { $unwind: '$routes.stops' },
              {
                $project: {
                  runId: '$_id',
                  simulationType: '$simulationType',
                  ticketId: '$routes.stops.ticketId',
                },
              },
              { $match: { ticketId: { $ne: null } } },
              {
                $unionWith: {
                  coll: RouteOptimizationResult._collectionName,
                  pipeline: [
                    { $match: { simulationType: { $ne: null } } },
                    ...(createdAtMatch ? [{ $match: createdAtMatch }] : []),
                    { $unwind: '$unscheduledTickets' },
                    {
                      $project: {
                        runId: '$_id',
                        simulationType: '$simulationType',
                        ticketId: '$unscheduledTickets.id',
                      },
                    },
                    { $match: { ticketId: { $ne: null } } },
                  ],
                },
              },
              {
                $group: {
                  _id: { runId: '$runId', simulationType: '$simulationType' },
                  ticketIds: { $addToSet: '$ticketId' },
                },
              },
              {
                $project: {
                  simulationType: '$_id.simulationType',
                  plannedTicketsCount: { $size: '$ticketIds' },
                },
              },
              {
                $set: {
                  simulationType: {
                    $cond: [
                      {
                        $regexMatch: {
                          input: '$simulationType',
                          regex: /^rerun-/,
                        },
                      },
                      'rerun',
                      '$simulationType',
                    ],
                  },
                },
              },
              {
                $group: {
                  _id: '$simulationType',
                  plannedTicketsCount: { $sum: '$plannedTicketsCount' },
                },
              },
              { $sort: { _id: 1 } },
            ])
            .toArray();

          return results.map(
            r =>
              new TicketPlanningGlobalStatistic({
                simulationType: r._id,
                plannedTicketsCount: r.plannedTicketsCount,
              }),
          );
        }),
      ),
    );
  },
  params: [
    { name: 'month', type: () => Int },
    { name: 'year', type: () => Int },
  ],
});
